import { Box, SimpleGrid, Flex, Spinner, useToast } from "@chakra-ui/react";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "components/card/Card.js";
import ReportFilters from "./components/ReportFilters";
import ReportStatCards from "./components/ReportStatCards";
import PublishingTrendChart from "./components/PublishingTrendChart";
import BroadcastTrendChart from "./components/BroadcastTrendChart";
import ViewsByCategoryChart from "./components/ViewsByCategoryChart";
import TopNewsTable from "./components/TopNewsTable";
import BroadcastStatsPanel from "./components/BroadcastStatsPanel";
import ContentOverviewPanel from "./components/ContentOverviewPanel";
import {
  getOverview,
  getTopNews,
  getViewsByCategory,
  getPublishingTrend,
  getBroadcastStats,
  getBroadcastTrend,
  getContentOverview,
} from "./reportsApi";

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

// Day/Week/Month presets pair a bucket size with a matching auto-computed
// window: last 30 days, last 7 days, last 1 month. Custom range
// (isCustomRange=true, handled where this is called) skips this entirely
// and leaves from/to under manual control instead.
function computeRangeForInterval(interval) {
  const to = new Date();
  const from = new Date(to);
  if (interval === "week") from.setDate(to.getDate() - 7);
  else if (interval === "month") from.setMonth(to.getMonth() - 1);
  else from.setDate(to.getDate() - 30);
  return { from: toDateInputValue(from), to: toDateInputValue(to) };
}

export default function MainDashboard() {
  const { t } = useTranslation();
  const toast = useToast();

  const [interval, setInterval] = useState("day");
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [from, setFrom] = useState(() => computeRangeForInterval("day").from);
  const [to, setTo] = useState(() => computeRangeForInterval("day").to);

  const selectPreset = (presetInterval) => {
    const nextRange = computeRangeForInterval(presetInterval);
    setInterval(presetInterval);
    setIsCustomRange(false);
    setFrom(nextRange.from);
    setTo(nextRange.to);
  };

  // Custom range doesn't expose a bucket picker — fixed at "day" (the
  // finest granularity) since there's no preset button to imply one here.
  const selectCustom = () => {
    setInterval("day");
    setIsCustomRange(true);
  };

  const resetFilters = () => selectPreset("day");

  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [topNews, setTopNews] = useState([]);
  const [viewsByCategory, setViewsByCategory] = useState([]);
  const [publishingTrend, setPublishingTrend] = useState([]);
  const [broadcastStats, setBroadcastStats] = useState(null);
  const [broadcastTrend, setBroadcastTrend] = useState([]);
  const [contentOverview, setContentOverview] = useState(null);

  const range = { from, to };

  const fetchRangedReports = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewData, topNewsData, viewsByCategoryData, publishingTrendData, broadcastStatsData, broadcastTrendData] =
        await Promise.all([
          getOverview(range),
          getTopNews({ ...range, limit: 10 }),
          getViewsByCategory(range),
          getPublishingTrend({ ...range, interval }),
          getBroadcastStats(range),
          getBroadcastTrend({ ...range, interval }),
        ]);
      setOverview(overviewData);
      setTopNews(topNewsData);
      setViewsByCategory(viewsByCategoryData);
      setPublishingTrend(publishingTrendData);
      setBroadcastStats(broadcastStatsData);
      setBroadcastTrend(broadcastTrendData);
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to fetch reports."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, interval, t, toast]);

  useEffect(() => {
    fetchRangedReports();
  }, [fetchRangedReports]);

  // All-time, not affected by the date-range filter — fetched once.
  useEffect(() => {
    getContentOverview()
      .then(setContentOverview)
      .catch((error) => {
        toast({
          title: t("An error occurred."),
          description: error.message || t("Failed to fetch content overview."),
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card w="100%" p="20px" mb="20px">
        <ReportFilters
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          interval={interval}
          isCustomRange={isCustomRange}
          onSelectPreset={selectPreset}
          onSelectCustom={selectCustom}
          onReset={resetFilters}
        />
      </Card>

      {loading && !overview ? (
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="xl" />
        </Flex>
      ) : (
        <>
          <ReportStatCards overview={overview} />

          <Box mb="20px">
            <PublishingTrendChart rows={publishingTrend} interval={interval} />
          </Box>

          <SimpleGrid columns={{ base: 1, xl: 2 }} gap="20px" mb="20px">
            <ViewsByCategoryChart rows={viewsByCategory} />
            <BroadcastTrendChart rows={broadcastTrend} interval={interval} />
          </SimpleGrid>

          <Box mb="20px">
            <TopNewsTable rows={topNews} />
          </Box>

          <Box mb="20px">
            <BroadcastStatsPanel stats={broadcastStats} />
          </Box>

          <Box mb="20px">
            <ContentOverviewPanel overview={contentOverview} />
          </Box>
        </>
      )}
    </Box>
  );
}
